'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect, useRef, FormEvent } from 'react';
import ArrowIcon from '@/components/ArrowIcon';
import ExitIntentPopup from '@/components/ExitIntentPopup';
import { trackLead } from '@/lib/tracking';

// SVG Icons
const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const SuccessIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const LoadingSpinner = () => (
  <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4 31.4" strokeLinecap="round">
      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
    </circle>
  </svg>
);

// Benefit icons
const SpeedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/>
  </svg>
);

const SeoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <path d="m21 21-4.3-4.3"/>
    <path d="m11 8v6"/>
    <path d="m8 11h6"/>
  </svg>
);

const ResponsiveIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect width="10" height="14" x="3" y="8" rx="2"/>
    <path d="M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-5"/>
    <path d="M9 18h6"/>
  </svg>
);

const AdminIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

// Animated counter component
function CountUpNumber({ value }: { value: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [displayed, setDisplayed] = useState(value);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const match = value.match(/^(\d+)(.*)$/);
    if (!match) return;

    const target = parseInt(match[1], 10);
    const suffix = match[2];

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const duration = 1800;
          const start = performance.now();

          const animate = (now: number) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = Math.round(eased * target);
            setDisplayed(`${current}${suffix}`);
            if (progress < 1) requestAnimationFrame(animate);
          };

          setDisplayed(`0${suffix}`);
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  return <p ref={ref} className="why-number">{displayed}</p>;
}

// FAQ Accordion
function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="faq-item">
      <button
        className={`faq-question ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
        aria-expanded={isOpen}
      >
        <h3>{question}</h3>
        <svg
          className="faq-icon"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
      <div className={`faq-answer ${isOpen ? 'open' : ''}`} role="region">
        <p>{answer}</p>
      </div>
    </div>
  );
}

interface PricingPackage {
  id: string;
  name: string;
  subtitle: string | null;
  pricePrefix: string;
  price: number;
  priceSuffix: string;
  features: string;
  alerts: string;
  ctaText: string;
  ctaLink: string;
  isHighlighted: boolean;
}

interface Project {
  id: string;
  title: string;
  projectUrl: string;
  imageUrl: string;
  tags: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
  consent?: string;
}

interface WebyNaMiruContentProps {
  packages: PricingPackage[];
  projects: Project[];
  trackingConfig: {
    googleAdsId: string | null;
    googleAdsConversionLabel: string | null;
  };
}

export default function WebyNaMiruContent({ packages, projects, trackingConfig }: WebyNaMiruContentProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    budget: '',
    message: '',
    consent: false,
  });
  const [honeypot, setHoneypot] = useState('');
  const [formLoadedAt] = useState(Date.now());
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [budgetDropdownOpen, setBudgetDropdownOpen] = useState(false);
  const budgetDropdownRef = useRef<HTMLDivElement>(null);

  const budgetOptions = [
    { value: '', label: 'Vyberte rozpočet' },
    { value: 'do 25 000 Kč', label: 'do 25 000 Kč' },
    { value: '25 000 - 60 000 Kč', label: '25 000 - 60 000 Kč' },
    { value: '60 000 - 100 000 Kč', label: '60 000 - 100 000 Kč' },
    { value: 'nad 100 000 Kč', label: 'nad 100 000 Kč' },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (budgetDropdownRef.current && !budgetDropdownRef.current.contains(event.target as Node)) {
        setBudgetDropdownOpen(false);
      }
    }

    if (budgetDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [budgetDropdownOpen]);

  const validateField = (name: string, value: string | boolean): string | undefined => {
    switch (name) {
      case 'name':
        if (!value || (typeof value === 'string' && value.trim().length < 2)) {
          return 'Jméno musí mít alespoň 2 znaky';
        }
        break;
      case 'email':
        if (!value || (typeof value === 'string' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))) {
          return 'Zadejte platný e-mail';
        }
        break;
      case 'message':
        if (!value || (typeof value === 'string' && value.trim().length < 10)) {
          return 'Zpráva musí mít alespoň 10 znaků';
        }
        break;
      case 'consent':
        if (!value) {
          return 'Musíte souhlasit se zpracováním údajů';
        }
        break;
    }
    return undefined;
  };

  const handleBlur = (name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name as keyof typeof formData]);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleChange = (name: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const newErrors: FormErrors = {};
    (['name', 'email', 'message', 'consent'] as const).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key as keyof FormErrors] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched({ name: true, email: true, message: true, consent: true });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || undefined,
          budget: formData.budget || undefined,
          message: formData.message,
          source: 'landing-page',
          website: honeypot,
          _t: formLoadedAt,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', budget: '', message: '', consent: false });
        setTouched({});
        setErrors({});

        // Facebook Pixel Lead tracking
        trackLead({
          source: 'landing-weby',
          name: formData.name,
          email: formData.email,
          budget: formData.budget || undefined,
        });

        // Google Ads conversion tracking
        if (typeof window !== 'undefined' && typeof window.gtag === 'function' && trackingConfig.googleAdsId && trackingConfig.googleAdsConversionLabel) {
          window.gtag('event', 'conversion', {
            send_to: `${trackingConfig.googleAdsId}/${trackingConfig.googleAdsConversionLabel}`,
            value: 1.0,
            currency: 'CZK',
          });
        }
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }

    setIsSubmitting(false);
  };

  const scrollToForm = () => {
    document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' });
  };

  const benefits = [
    {
      icon: <SpeedIcon />,
      title: 'Přehledná struktura, která vede k akci',
      description: 'Návštěvník okamžitě ví, co děláte a co má udělat. Žádné bloudění, žádné zbytečné kroky.',
    },
    {
      icon: <SeoIcon />,
      title: 'Připravený na SEO od prvního dne',
      description: 'Správná struktura, rychlost a čistý kód. Základ pro to, aby vás zákazníci našli na Googlu.',
    },
    {
      icon: <ResponsiveIcon />,
      title: 'Rychlé načítání, žádné odcházející návštěvy',
      description: 'Pomalý web = ztracení zákazníci. Váš web se načte rychle na mobilu i počítači.',
    },
    {
      icon: <AdminIcon />,
      title: 'Snadná správa obsahu bez vývojáře',
      description: 'Texty, fotky, ceník — změníte si sami za pár minut. Bez volání, bez čekání.',
    },
  ];

  const processSteps = [
    { number: 1, title: 'Konzultace zdarma', description: 'Řeknete mi o svém byznysu, já navrhnu řešení. Bez závazků.' },
    { number: 2, title: 'Návrh a odsouhlasení', description: 'Uvidíte grafický návrh, než se napíše jediný řádek kódu.' },
    { number: 3, title: 'Vývoj webu', description: 'Programuji, průběžně ukazuji postup. Žádná překvapení.' },
    { number: 4, title: 'Spuštění a předání', description: 'Web jde ostře. Dostanete přístupy a zaškolení do administrace.' },
  ];

  const faqItems = [
    { question: 'Jak dlouho trvá vytvoření webu?', answer: 'Jednoduchý firemní web je hotový za 1–2 týdny. Rozsáhlejší projekty trvají 3–6 týdnů. Přesný termín znáte předem.' },
    { question: 'Co ode mě budete potřebovat?', answer: 'Texty, fotky a logo. Pokud je nemáte, pomohu s jejich zajištěním — není to překážka.' },
    { question: 'Budu moct obsah měnit sám?', answer: 'Ano. Každý web má přehlednou administraci, kde změníte texty, fotky nebo ceník během pár minut.' },
    { question: 'Platím nějaké měsíční poplatky?', answer: 'Ne. Platíte jednorázově za tvorbu webu. Jediný náklad navíc je hosting (cca 100–300 Kč/měsíc) a doména (cca 200 Kč/rok).' },
    { question: 'Co když budu časem potřebovat úpravy?', answer: 'Jsem k dispozici i po spuštění. Nabízím jednorázové úpravy nebo měsíční správu, podle toho, co vám vyhovuje.' },
    { question: 'Proč ne Wordpress nebo šablona?', answer: 'Šablony jsou rychlé, ale omezené. Web na míru je rychlejší, bezpečnější a přesně odpovídá tomu, co váš byznys potřebuje. A nemusíte řešit aktualizace pluginů.' },
  ];

  return (
    <>
      {/* Hero Section */}
      <header className="landing-hero">
        {/* Geometric decorations */}
        <div className="geo-shapes" aria-hidden="true">
          <svg className="geo-ring" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="100" cy="100" r="90" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
            <circle cx="100" cy="100" r="60" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
          </svg>
          <svg className="geo-cross" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="50" y1="0" x2="50" y2="100" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
            <line x1="0" y1="50" x2="100" y2="50" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
          </svg>
          <svg className="geo-dots" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            {[0, 30, 60, 90].map(x => [0, 30, 60, 90].map(y => (
              <circle key={`${x}-${y}`} cx={x + 15} cy={y + 15} r="2" fill="rgba(255,255,255,0.2)" />
            )))}
          </svg>
          <div className="geo-line-diagonal"></div>
          <svg className="geo-ring-left" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="100" cy="100" r="90" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
            <circle cx="100" cy="100" r="65" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
            <circle cx="100" cy="100" r="40" stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
          </svg>
          <svg className="geo-triangle" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <polygon points="50,5 95,95 5,95" stroke="rgba(255,255,255,0.18)" strokeWidth="1" fill="none" />
          </svg>
          <div className="geo-line-left"></div>
          <svg className="geo-dots-left" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            {[0, 25, 50, 75].map(x => [0, 25, 50, 75].map(y => (
              <circle key={`l-${x}-${y}`} cx={x + 12} cy={y + 12} r="1.5" fill="rgba(255,255,255,0.2)" />
            )))}
          </svg>
          <svg className="geo-diamond" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="29" y="29" width="42" height="42" stroke="rgba(255,255,255,0.15)" strokeWidth="1" fill="none" transform="rotate(45 50 50)" />
          </svg>
        </div>

        <div className="landing-hero-content">
          <div className="landing-hero-text header-animate">
            <h1 className="anim-item">
              Web, který vám přivádí zákazníky, <span className="highlight">ne jen návštěvy</span>
            </h1>
            <p className="landing-hero-subtitle anim-item">
              Navrhuji weby, které dávají smysl vašemu byznysu.<br />
              Přehledné, rychlé a postavené tak, aby z návštěvníků dělaly zákazníky.
            </p>
            <div className="landing-hero-cta anim-item">
              <button onClick={scrollToForm} className="btn-white">
                Získat návrh řešení zdarma
              </button>
              <button onClick={() => document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' })} className="btn-text">
                Ukázky realizací
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Social Proof - Stats */}
      <div className="why why-landing" id="why">
        <div className="why-content">
          <div className="why-box">
            <div className="info">
              <CountUpNumber value="12+" />
              <p>let na trhu</p>
            </div>
          </div>
          <div className="why-box">
            <div className="info">
              <CountUpNumber value="250+" />
              <p>dokončených webů</p>
            </div>
          </div>
          <div className="why-box">
            <div className="info">
              <CountUpNumber value="120+" />
              <p>spokojených firem</p>
            </div>
          </div>
        </div>
      </div>

      {/* Why New Web - Pain Points */}
      <section className="why-new-web">
        <div className="why-new-web-content">
          <div className="why-new-web-header">
            <span className="section-label">Poznáváte se?</span>
            <h2>Váš web vás brzdí, když...</h2>
            <p>Každý z těchto bodů vás stojí reálné peníze a zákazníky.</p>
          </div>
          <ul className="pain-points">
            <li>
              <span className="pain-icon" aria-hidden="true">✕</span>
              Lidé přijdou, ale nikdo nepoptá
            </li>
            <li>
              <span className="pain-icon" aria-hidden="true">✕</span>
              Web působí nedůvěryhodně
            </li>
            <li>
              <span className="pain-icon" aria-hidden="true">✕</span>
              Není jasné, co má návštěvník vlastně udělat
            </li>
            <li>
              <span className="pain-icon" aria-hidden="true">✕</span>
              Web nevede návštěvníky k akci
            </li>
            <li>
              <span className="pain-icon" aria-hidden="true">✕</span>
              Na mobilu nefunguje nebo vypadá špatně
            </li>
            <li>
              <span className="pain-icon" aria-hidden="true">✕</span>
              Konkurence s lepším webem vám přebírá zákazníky
            </li>
          </ul>
          <button onClick={scrollToForm} className="btn-primary-large">Chci web, který tohle řeší</button>
        </div>
      </section>

      {/* Benefits */}
      <section className="benefits">
        <div className="benefits-content">
          <div className="benefits-header">
            <span className="section-label">Co získáte</span>
            <h2>Web, který vám vydělává</h2>
            <p>Nejde jen o to, jak web vypadá. Jde o to, kolik zákazníků vám přivede.</p>
          </div>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-box">
                <div className="benefit-icon" aria-hidden="true">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
          <div className="benefits-cta">
            <button onClick={scrollToForm} className="btn-primary-large">
              Chci takový web
            </button>
          </div>
        </div>
      </section>

      {/* Strategy Quote */}
      <section className="strategy-quote">
        <div className="strategy-quote-content">
          <blockquote>
            <p>
              Nezačínám designem, ale otázkami. Kdo je váš zákazník? Co ho trápí? Proč by si měl vybrat vás? Teprve pak vzniká web — ne jako hezká vizitka, ale jako nástroj, který firmě přináší zakázky.
            </p>
          </blockquote>
        </div>
      </section>

      {/* Process */}
      <section className="process">
        <div className="process-content">
          <div className="process-header">
            <span className="process-label">Jak spolupracujeme</span>
            <h2>4 kroky k novému webu</h2>
            <p>Jasný postup, ve kterém víte, co se děje, co potřebuji od vás a kdy bude hotovo.</p>
          </div>
          <div className="process-grid">
            {processSteps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="process-step">
                  <div className="step-top">
                    <span className="step-label">Krok {String(step.number).padStart(2, '0')}</span>
                    <h3>{step.title}</h3>
                  </div>
                  <p>{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="process-arrow" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14" />
                      <path d="m12 5 7 7-7 7" />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="portfolio-preview" id="portfolio">
        <div className="portfolio-preview-content">
          <div className="portfolio-header">
            <span className="portfolio-label">Ukázky realizací</span>
            <h2>Weby, které firmám pomáhají růst</h2>
            <p>Každý z těchto webů vznikl s jedním cílem — přivést majiteli víc zákazníků.</p>
          </div>
          <div className="portfolio-grid-landing">
            {projects.slice(0, 4).map((project) => {
              let tags: string[] = [];
              try { tags = JSON.parse(project.tags); } catch { /* empty */ }
              return (
                <div key={project.id} className="portfolio-box">
                  <a target="_blank" href={project.projectUrl} className="link" rel="noopener noreferrer">
                    <div className="portfolio-box-inner">
                      <Image src={project.imageUrl} alt={`${project.title} - ukázka webu`} width={800} height={600} sizes="(max-width: 768px) 100vw, 50vw" />
                      <div className="overlay"></div>
                      <div className="overlay2"></div>
                      <div className="info">
                        <h3>{project.title}</h3>
                        <ul>
                          {tags.map((tag, index) => (
                            <li key={index}>{tag}</li>
                          ))}
                        </ul>
                        <Image className="arrow" src="/images/icons/icons8-right-arrow-32.png" alt="" width={32} height={32} aria-hidden="true" />
                      </div>
                    </div>
                  </a>
                </div>
              );
            })}
          </div>
          <div className="portfolio-button">
            <Link href="/portfolio">
              <button className="btn w-arrow light" aria-label="zobrazit projekty">
                <span>Zobrazit další projekty</span>
                <div className="arrows" aria-hidden="true">
                  <ArrowIcon />
                  <ArrowIcon />
                </div>
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Simple Pricing */}
      <section className="simple-pricing" id="cenik">
        <div className="simple-pricing-content">
          <div className="simple-pricing-header">
            <span className="section-label">Investice</span>
            <h2>Web na míru od 22 000 Kč</h2>
            <p>Řešení, které odpovídá vašemu byznysu, není závislé na šablonách a můžete ho dál rozvíjet.</p>
          </div>
          <ul className="pricing-features-grid">
            <li>
              <span className="feature-icon" aria-hidden="true"><CheckIcon /></span>
              Design na míru
            </li>
            <li>
              <span className="feature-icon" aria-hidden="true"><CheckIcon /></span>
              Responzivní verze
            </li>
            <li>
              <span className="feature-icon" aria-hidden="true"><CheckIcon /></span>
              SEO optimalizace
            </li>
            <li>
              <span className="feature-icon" aria-hidden="true"><CheckIcon /></span>
              Vlastní administrace
            </li>
            <li>
              <span className="feature-icon" aria-hidden="true"><CheckIcon /></span>
              Kontaktní formulář
            </li>
            <li>
              <span className="feature-icon" aria-hidden="true"><CheckIcon /></span>
              SSL certifikát
            </li>
          </ul>
          <div className="pricing-cta-row">
            <button onClick={scrollToForm} className="btn-primary-large">
              Získat návrh řešení zdarma
            </button>
            <Link href="/cenik" className="btn-text-dark">
              Potřebuji e-shop
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq">
        <div className="faq-content">
          <div className="faq-header">
            <span className="faq-label">Máte dotazy?</span>
            <h2>Často kladené otázky</h2>
          </div>
          <div className="faq-list">
            {faqItems.map((item, index) => (
              <FaqItem key={index} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* Inline Contact Form */}
      <section className="inline-form" id="kontakt">
        <div className="inline-form-content">
          <div className="inline-form-header">
            <span className="form-label">Začněte teď</span>
            <h2>Kolik zákazníků vás váš web stojí každý měsíc?</h2>
            <p>Napište mi a do 24 hodin se ozvu. Konzultace je zdarma a bez závazků.</p>
          </div>

          {submitStatus === 'success' ? (
            <div className="form-success">
              <div className="success-check" aria-hidden="true">
                <svg viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="26" cy="26" r="25" stroke="#fff" strokeWidth="2"/>
                  <path d="M15 27l7 7 15-15" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Děkuji za zprávu!</h3>
              <p>Ozvu se Vám co nejdříve, obvykle do 24 hodin.</p>
              <button className="btn-primary-large" onClick={() => setSubmitStatus('idle')}>
                Odeslat další zprávu
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="landing-form" noValidate>
              <input type="text" name="website" value={honeypot} onChange={e => setHoneypot(e.target.value)} style={{ position: 'absolute', left: '-9999px', tabIndex: -1 } as React.CSSProperties} autoComplete="off" aria-hidden="true" />
              <div className="form-row">
                <div className={`form-group ${errors.name && touched.name ? 'has-error' : ''}`}>
                  <label htmlFor="name">Jméno a příjmení *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    autoComplete="name"
                    placeholder="Jan Novák"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    onBlur={() => handleBlur('name')}
                  />
                  {errors.name && touched.name && (
                    <span className="field-error" role="alert">{errors.name}</span>
                  )}
                </div>

                <div className={`form-group ${errors.email && touched.email ? 'has-error' : ''}`}>
                  <label htmlFor="email">E-mail *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    autoComplete="email"
                    placeholder="jan@example.cz"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    onBlur={() => handleBlur('email')}
                  />
                  {errors.email && touched.email && (
                    <span className="field-error" role="alert">{errors.email}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Telefon <span className="optional">(nepovinné)</span></label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    autoComplete="tel"
                    placeholder="+420 777 888 999"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label id="budget-label">Rozpočet <span className="optional">(nepovinné)</span></label>
                  <div
                    ref={budgetDropdownRef}
                    className={`custom-select ${budgetDropdownOpen ? 'open' : ''}`}
                  >
                    <button
                      type="button"
                      className="custom-select-trigger"
                      onClick={() => setBudgetDropdownOpen(!budgetDropdownOpen)}
                      aria-labelledby="budget-label"
                      aria-expanded={budgetDropdownOpen}
                      aria-haspopup="listbox"
                    >
                      <span>{formData.budget || 'Vyberte rozpočet'}</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m6 9 6 6 6-6"/>
                      </svg>
                    </button>
                    {budgetDropdownOpen && (
                      <div className="custom-select-options" role="listbox" aria-labelledby="budget-label">
                        {budgetOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            role="option"
                            aria-selected={formData.budget === option.value}
                            className={`custom-select-option ${formData.budget === option.value ? 'selected' : ''}`}
                            onClick={() => {
                              handleChange('budget', option.value);
                              setBudgetDropdownOpen(false);
                            }}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={`form-group ${errors.message && touched.message ? 'has-error' : ''}`}>
                <label htmlFor="message">Zpráva *</label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Popište svůj projekt — co potřebujete, jaké máte představy..."
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  onBlur={() => handleBlur('message')}
                  rows={5}
                />
                {errors.message && touched.message && (
                  <span className="field-error" role="alert">{errors.message}</span>
                )}
              </div>

              <div className={`form-check ${errors.consent && touched.consent ? 'has-error' : ''}`}>
                <label className="checkbox-container">
                  <input
                    type="checkbox"
                    checked={formData.consent}
                    onChange={(e) => handleChange('consent', e.target.checked)}
                    onBlur={() => handleBlur('consent')}
                  />
                  <span className="checkbox-mark"></span>
                  <span className="checkbox-text">
                    Souhlasím se zpracováním osobních údajů *
                  </span>
                </label>
                {errors.consent && touched.consent && (
                  <span className="field-error" role="alert">{errors.consent}</span>
                )}
              </div>

              <button className="btn-submit-large" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <LoadingSpinner />
                    <span>Odesílám...</span>
                  </>
                ) : (
                  <span>Odeslat poptávku</span>
                )}
              </button>

              {submitStatus === 'error' && (
                <div className="form-error" role="alert">
                  <p>Něco se pokazilo. Zkuste to prosím znovu.</p>
                </div>
              )}
            </form>
          )}
        </div>
      </section>

      {/* Exit Intent Popup */}
      <ExitIntentPopup onCtaClick={scrollToForm} />
    </>
  );
}
